<template>
  <Menu>
    <template #label="{ toggle, isOpen }">
      <Backdrop :show="isOpen" class="z-50" @click="toggle" />
      <div class="flex justify-end">
        <Button
          :type="buttonType"
          :is-active="isActive"
          data-test-id="sorting-menu-toggle-button"
          @click="toggle"
        >
          <template #icon>
            <IconUpdown
              v-if="
                !showNewDesktopPlpQuickFilters || isMobileOrTablet || isActive
              "
              class="size-5"
            />
            <IconUpdownGray v-else class="size-5" />
          </template>
          {{ $t(`sorting_select.sort_by`) }}
        </Button>
      </div>
    </template>

    <template #menu-content="{ close }">
      <FadeInTransition>
        <FocusTrap>
          <div
            class="mt-5 bg-white pt-4 text-center shadow"
            @keydown.esc="close"
          >
            <SortingItemsList
              ref="listRef"
              :items="values"
              :selected="selectedSortName"
              class="w-full space-y-2 px-6"
              @click:item="close"
            />
            <AgsLink
              class="mt-6 w-full appearance-none justify-center gap-1 border-t py-3"
              size="base"
              :to="
                toLocalePath({
                  params: { ...$route.params },
                  query: { ...$route.query, sort: undefined, page: undefined },
                })
              "
            >
              <IconReset class="h-3" />
              {{ $t('sorting_select.reset') }}
            </AgsLink>
          </div>
        </FocusTrap>
      </FadeInTransition>
    </template>
  </Menu>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { FocusTrap } from 'focus-trap-vue'
import useExperiments from '~/composables/useExperiments'
import useRouteHelpers from '../../composables/useRouteHelpers'
import useSorting from '../../composables/useSorting'
import type { SortValue } from '../../utils/sort'
import { useDeviceType } from '~/composables/useDeviceType'
import { SortingItemsList } from '#components'

defineProps<{ values: SortValue[] }>()

const { selectedSortName } = useSorting()
const { toLocalePath } = useRouteHelpers()
const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobileOrTablet } = useDeviceType()

const buttonType = computed(() =>
  showNewDesktopPlpQuickFilters.value && !isMobileOrTablet.value
    ? 'muted'
    : 'tertiary',
)
const isActive = computed(() => selectedSortName.value !== 'topseller')

const listRef = ref<InstanceType<typeof SortingItemsList> | null>(null)

watch(
  () => listRef.value,
  async (list) => {
    if (list) {
      await nextTick()
      const firstItem = list.$el?.querySelector(
        '[tabindex], button, a, input, [role="menuitem"]',
      )
      if (firstItem) firstItem.focus()
    }
  },
)
</script>
